import { useCallback, useState } from 'react';
import { api } from '../api/client';
import { useMutation, useResource } from '../api/hooks';
import type {
  AbuseSignal,
  AdminAccount,
  AdminAccountDetail,
  CatalogModule,
  EntitlementKind,
} from '../api/types';
import { useI18n } from '../i18n';
import { Badge, Button, Card, Empty, Field, Select, Spinner, TextInput } from '../components/ui';
import { ErrorBanner } from '../components/ErrorBanner';
import { ConfirmButton } from '../components/Confirm';

type Tab = 'accounts' | 'signals' | 'modules';

export function AdminPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('accounts');

  return (
    <div className="stack">
      <h1>{t('admin.title')}</h1>
      <div className="tabs">
        <button
          className={tab === 'accounts' ? 'tab active' : 'tab'}
          onClick={() => setTab('accounts')}
        >
          {t('admin.accounts')}
        </button>
        <button
          className={tab === 'signals' ? 'tab active' : 'tab'}
          onClick={() => setTab('signals')}
        >
          {t('admin.signals')}
        </button>
        <button
          className={tab === 'modules' ? 'tab active' : 'tab'}
          onClick={() => setTab('modules')}
        >
          {t('admin.modules')}
        </button>
      </div>

      {tab === 'accounts' ? <AccountsTab /> : null}
      {tab === 'signals' ? <SignalsTab /> : null}
      {tab === 'modules' ? <ModulesTab /> : null}
    </div>
  );
}

function AccountsTab() {
  const { t } = useI18n();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const results = useResource<AdminAccount[]>(
    query ? `/v1/admin/accounts?q=${encodeURIComponent(query)}` : null,
  );
  const detail = useResource<AdminAccountDetail>(
    selected ? `/v1/admin/accounts/${selected}` : null,
  );

  return (
    <div className="stack">
      <Card title={t('admin.accounts')}>
        <div className="row">
          <TextInput
            value={term}
            placeholder={t('admin.searchPlaceholder')}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setQuery(term.trim());
            }}
          />
          <Button variant="primary" onClick={() => setQuery(term.trim())}>
            {t('common.search')}
          </Button>
        </div>
        <ErrorBanner error={results.error} />
        {results.loading ? <Spinner label={t('common.loading')} /> : null}
        <ul className="list">
          {(results.data ?? []).map((account) => (
            <li key={account.id} className="list-row">
              <span>
                {account.globalName ?? account.username}{' '}
                <code className="muted">{account.discordId}</code>{' '}
                {account.status !== 'ACTIVE' ? <Badge tone="bad">{account.status}</Badge> : null}
              </span>
              <Button onClick={() => setSelected(account.id)}>{t('common.search')}</Button>
            </li>
          ))}
        </ul>
      </Card>

      {selected && detail.data ? (
        <AccountDetail detail={detail.data} onChange={detail.reload} />
      ) : null}
    </div>
  );
}

function AccountDetail({ detail, onChange }: { detail: AdminAccountDetail; onChange: () => void }) {
  const { t, formatDate } = useI18n();
  const modules = useResource<CatalogModule[]>('/v1/admin/modules');

  const [quota, setQuota] = useState(detail.serverQuota);
  const [kind, setKind] = useState<EntitlementKind>('MODULE_PERPETUAL');
  const [moduleId, setModuleId] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const setQuotaMutation = useMutation<number, unknown>(
    useCallback(
      (value: number) =>
        api.patch(`/v1/admin/accounts/${detail.id}/quota`, {
          serverQuota: value,
          reason: 'ajustement support',
        }),
      [detail.id],
    ),
  );
  const grant = useMutation<Record<string, unknown>, unknown>(
    useCallback((body) => api.post('/v1/admin/entitlements', body), []),
  );
  const act = useMutation<{ id: string; action: string }, unknown>(
    useCallback(
      (input) =>
        api.post(`/v1/admin/entitlements/${input.id}/${input.action}`, {
          reason: reason.trim() || 'action support',
        }),
      [reason],
    ),
  );

  const allAccess = kind === 'ALL_ACCESS_SUBSCRIPTION';
  const subscription = kind !== 'MODULE_PERPETUAL';

  const submitGrant = async () => {
    const body: Record<string, unknown> = { accountId: detail.id, kind };
    if (!allAccess) body.moduleId = moduleId;
    if (subscription) body.periodEnd = new Date(periodEnd).toISOString();
    if (note.trim()) body.note = note.trim();
    const created = await grant.run(body);
    if (created) {
      setNote('');
      onChange();
    }
  };

  return (
    <>
      <Card title={detail.globalName ?? detail.username} hint={detail.email ?? undefined}>
        <dl className="pairs">
          <dt>Discord</dt>
          <dd>
            <code>{detail.discordId}</code>
          </dd>
          <dt>{t('admin.quota')}</dt>
          <dd>
            <div className="row">
              <TextInput
                type="number"
                min={0}
                max={50}
                value={quota}
                onChange={(event) => setQuota(Number(event.target.value))}
              />
              <Button
                pending={setQuotaMutation.pending}
                onClick={() => void setQuotaMutation.run(quota).then(onChange)}
              >
                {t('admin.quotaUpdate')}
              </Button>
            </div>
          </dd>
          <dt>{t('admin.ownedServers')}</dt>
          <dd>{detail.ownedServers.map((server) => server.name).join(', ') || t('common.none')}</dd>
        </dl>
        <ErrorBanner error={setQuotaMutation.error} />
      </Card>

      <Card title={t('admin.entitlements')}>
        <ErrorBanner error={act.error} />
        <Field label={t('admin.reason')}>
          <TextInput value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <ul className="list">
          {detail.entitlements.map((entitlement) => (
            <li key={entitlement.id} className="list-row">
              <span>
                <Badge tone={entitlement.status === 'ACTIVE' ? 'good' : 'neutral'}>
                  {entitlement.status}
                </Badge>{' '}
                {entitlement.moduleKey ?? t('licenses.allAccess')}
                {entitlement.activeServer ? ` → ${entitlement.activeServer.name}` : ''}
                {entitlement.currentPeriodEnd
                  ? ` · ${formatDate(entitlement.currentPeriodEnd)}`
                  : ''}
                {entitlement.cooldownUntil ? ' · ⏳' : ''}
              </span>
              <span className="row">
                {entitlement.cooldownUntil ? (
                  <Button
                    onClick={() =>
                      void act.run({ id: entitlement.id, action: 'clear-cooldown' }).then(onChange)
                    }
                  >
                    {t('admin.clearCooldown')}
                  </Button>
                ) : null}
                <ConfirmButton
                  message={t('admin.revoke')}
                  onConfirm={() =>
                    void act.run({ id: entitlement.id, action: 'revoke' }).then(onChange)
                  }
                >
                  {t('admin.revoke')}
                </ConfirmButton>
                <ConfirmButton
                  variant="ghost"
                  message={t('admin.refund')}
                  onConfirm={() =>
                    void act.run({ id: entitlement.id, action: 'refund' }).then(onChange)
                  }
                >
                  {t('admin.refund')}
                </ConfirmButton>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t('admin.grant')}>
        <ErrorBanner error={grant.error} />
        <Field label={t('admin.grantKind')}>
          <Select value={kind} onChange={(event) => setKind(event.target.value as EntitlementKind)}>
            <option value="MODULE_PERPETUAL">{t('licenses.perpetual')}</option>
            <option value="MODULE_SUBSCRIPTION">{t('licenses.subscription')}</option>
            <option value="ALL_ACCESS_SUBSCRIPTION">{t('licenses.allAccess')}</option>
          </Select>
        </Field>
        {!allAccess ? (
          <Field label={t('admin.grantModule')}>
            <Select value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">—</option>
              {(modules.data ?? []).map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name} ({module.key})
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {subscription ? (
          <Field label={t('admin.grantPeriodEnd')}>
            <TextInput
              type="datetime-local"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
            />
          </Field>
        ) : null}
        <Field label={t('admin.grantNote')}>
          <TextInput value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <Button
          variant="primary"
          pending={grant.pending}
          disabled={(!allAccess && !moduleId) || (subscription && !periodEnd)}
          onClick={() => void submitGrant()}
        >
          {t('admin.grant')}
        </Button>
      </Card>
    </>
  );
}

function SignalsTab() {
  const { t, formatDate } = useI18n();
  const signals = useResource<AbuseSignal[]>('/v1/admin/abuse-signals');
  const resolve = useMutation<string, unknown>(
    useCallback(
      (id: string) => api.post(`/v1/admin/abuse-signals/${id}/resolve`, { reason: 'traité' }),
      [],
    ),
  );
  const expiry = useMutation<void, unknown>(
    useCallback(() => api.post('/v1/admin/jobs/entitlement-expiry'), []),
  );

  const tone = (severity: number): 'neutral' | 'warn' | 'bad' =>
    severity >= 3 ? 'bad' : severity === 2 ? 'warn' : 'neutral';

  return (
    <div className="stack">
      <Card
        title={t('admin.signals')}
        actions={
          <Button pending={expiry.pending} onClick={() => void expiry.run()}>
            {t('admin.expiryJob')}
          </Button>
        }
      >
        <ErrorBanner error={signals.error ?? resolve.error ?? expiry.error} />
        {signals.loading ? <Spinner label={t('common.loading')} /> : null}
        {(signals.data ?? []).length === 0 && !signals.loading ? (
          <Empty>{t('admin.signalsEmpty')}</Empty>
        ) : null}
        <ul className="list">
          {(signals.data ?? []).map((signal) => (
            <li key={signal.id} className="list-row">
              <span>
                <Badge tone={tone(signal.severity)}>{signal.type}</Badge>{' '}
                {signal.server?.name ?? '—'}{' '}
                <span className="muted">
                  {t('admin.signalOccurrences', { count: signal.occurrences })} ·{' '}
                  {formatDate(signal.lastSeenAt)}
                </span>
              </span>
              <Button onClick={() => void resolve.run(signal.id).then(signals.reload)}>
                {t('admin.signalResolve')}
              </Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function ModulesTab() {
  const { t } = useI18n();
  const modules = useResource<CatalogModule[]>('/v1/admin/modules');
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [allAccess, setAllAccess] = useState(true);

  const create = useMutation<Record<string, unknown>, unknown>(
    useCallback((body) => api.post('/v1/admin/modules', body), []),
  );
  const update = useMutation<{ id: string; body: Record<string, unknown> }, unknown>(
    useCallback((input) => api.patch(`/v1/admin/modules/${input.id}`, input.body), []),
  );

  return (
    <div className="stack">
      <Card title={t('admin.modules')}>
        <ErrorBanner error={modules.error ?? update.error} />
        <ul className="list">
          {(modules.data ?? []).map((module) => (
            <li key={module.id} className="list-row">
              <span>
                <code>{module.key}</code> — {module.name}{' '}
                <Badge tone={module.status === 'ACTIVE' ? 'good' : 'neutral'}>
                  {module.status}
                </Badge>
                {module.includedInAllAccess ? <Badge>{t('admin.moduleAllAccess')}</Badge> : null}
              </span>
              <Select
                aria-label={t('admin.moduleStatus')}
                value={module.status}
                onChange={(event) =>
                  void update
                    .run({ id: module.id, body: { status: event.target.value } })
                    .then(modules.reload)
                }
              >
                <option value="DRAFT">DRAFT</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="DEPRECATED">DEPRECATED</option>
              </Select>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t('admin.moduleCreate')}>
        <ErrorBanner error={create.error} />
        <Field label={t('admin.moduleKey')} hint={t('admin.moduleKeyHint')}>
          <TextInput
            value={key}
            placeholder="scenarios"
            onChange={(event) => setKey(event.target.value.toLowerCase())}
          />
        </Field>
        <Field label={t('admin.moduleName')}>
          <TextInput value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <label className="row">
          <input
            type="checkbox"
            checked={allAccess}
            onChange={(event) => setAllAccess(event.target.checked)}
          />
          <span>{t('admin.moduleAllAccess')}</span>
        </label>
        <Button
          variant="primary"
          pending={create.pending}
          disabled={!/^[a-z0-9_]{2,32}$/.test(key) || name.trim().length < 2}
          onClick={() =>
            void create
              .run({ key, name: name.trim(), includedInAllAccess: allAccess, status: 'ACTIVE' })
              .then(() => {
                setKey('');
                setName('');
                modules.reload();
              })
          }
        >
          {t('common.create')}
        </Button>
      </Card>
    </div>
  );
}
